/**
 * Two-way SMS conversations — READ side only (plan §3, §5.2, §5.3, §9).
 *
 * Every call is a SECURITY DEFINER RPC; the tables are definer-only, so a direct table select
 * returns nothing. The server decides access on every call:
 *   - sms_conversations_available() is the gate for showing ANY entry point. It is true only for
 *     the conversation roles AND (the feature on, or internal proof + the caller allowlisted).
 *     The frontend flag `operatorSmsConversations` only decides whether we ask.
 *   - A conversation the caller may not open answers P0002 SMS_CONVERSATION_NOT_FOUND, the same as
 *     one that does not exist. The UI must not distinguish the two ("not rendered").
 *
 * There is NO send path here on purpose: replies are not built yet.
 */
import { supabase } from "./supabase";

export type SmsConversationTab = "mine" | "unassigned" | "all";

export interface SmsConversationRow {
  id: string;
  member_masked: string;
  status: "open" | "closed";
  owner_user_id: string | null;
  last_item_at: string | null;
  /** Only when exactly one association exists AND the caller may open that contact. */
  context_name: string | null;
  associations: number;
  unread: boolean;
  reminder: boolean;
  needs_reply: boolean;
}

/** An outbound (staff-sent) message, plan v10.1 §5.1. `concerning` is null when the viewer may not open that contact. */
export interface SmsOutboundInfo {
  state:
    | "pending"
    | "accepted"
    | "delivered"
    | "failed_definitive"
    | "uncertain";
  failure_reason: string | null;
  sent_by_name: string | null;
  concerning: string | null;
  via: "contact_page";
}

export interface SmsThreadItem {
  seq: number;
  kind: "inbound" | "outbound";
  /** set for inbound items */
  inbound_id: string | null;
  /** set for outbound items */
  tracking_id?: string | null;
  outbound?: SmsOutboundInfo | null;
  arrived_at: string;
  display_at: string;
  /** null = removed under the retention policy */
  body: string | null;
  classification: string | null;
  attachments: number;
  needs_reply: boolean;
}

export interface SmsOpenItem {
  seq: number;
  inbound_id: string;
  arrived_at: string;
  /** null = removed under the retention policy (normally never: open work is held) */
  body: string | null;
  change_id: number;
}

export interface SmsThread {
  conversation: {
    id: string;
    member_e164: string;
    member_masked: string;
    our_sender: string;
    status: "open" | "closed";
    owner_user_id: string | null;
    owner_revision: number;
    send_revision: number;
  };
  associations: number;
  /** Quick path: linked contacts this viewer may open (at most 5). Replies go through their Send SMS action. */
  reply_contacts?: { contact_id: string; name: string }[];
  /** linked contacts this viewer may not open (counted, never named) */
  restricted_contacts?: number;
  /** oldest -> newest */
  items: SmsThreadItem[];
  /** Unanswered questions, oldest first (at most 201), read from the server's open-question index -- including ones
   *  older than the page of `items`. Never capped by addressed messages (migration 036). */
  open_items: SmsOpenItem[];
  open_count: number;
  review_incomplete: boolean;
  snapshot_id: string;
  suppressed: boolean;
}

export interface SmsAckResult {
  ok: boolean;
  seen: number;
  floor: number;
  reminder_cleared: boolean;
}

export type SmsConversationErrorKind =
  | "disabled"
  | "not_authorized"
  | "not_found"
  | "stale"
  | "other";

/** A classified server refusal. `kind` is what the UI branches on; never show `message` raw. */
export class SmsConversationError extends Error {
  readonly kind: SmsConversationErrorKind;
  readonly code: string | undefined;
  constructor(kind: SmsConversationErrorKind, message: string, code?: string) {
    super(message);
    this.name = "SmsConversationError";
    this.kind = kind;
    this.code = code;
  }
}

interface PgError {
  code?: string;
  message?: string;
}

/** Map a PostgREST error to its contract kind (code first, message prefix as the tiebreak). */
export function classifySmsConversationError(
  err: unknown,
): SmsConversationError {
  const e = (err ?? {}) as PgError;
  const code = e.code;
  const msg = String(e.message ?? "");
  let kind: SmsConversationErrorKind = "other";
  if (code === "0A000" || msg.startsWith("SMS_CONVERSATIONS_DISABLED")) {
    kind = "disabled";
  } else if (
    code === "42501" ||
    msg.startsWith("SMS_CONVERSATIONS_NOT_AUTHORIZED")
  ) {
    kind = "not_authorized";
  } else if (code === "P0002" || msg.startsWith("SMS_CONVERSATION_NOT_FOUND")) {
    kind = "not_found";
  } else if (
    msg.startsWith("SMS_SNAPSHOT_STALE") ||
    msg.startsWith("SMS_SNAPSHOT_INVALID")
  ) {
    kind = "stale";
  }
  return new SmsConversationError(kind, msg || "Request failed", code);
}

async function call<T>(fn: string, args?: Record<string, unknown>): Promise<T> {
  const { data, error } = await supabase.rpc(fn, args);
  if (error) {
    throw classifySmsConversationError(error);
  }
  return data as T;
}

/**
 * Whether this user may see the conversation entry points at all. Any error (including the RPC
 * not existing in this environment) is "no": entry points are never shown on doubt.
 */
export async function smsConversationsAvailable(): Promise<boolean> {
  try {
    const { data, error } = await supabase.rpc("sms_conversations_available");
    if (error) {
      return false;
    }
    return data === true;
  } catch {
    return false;
  }
}

export async function listSmsConversations(
  tab: SmsConversationTab,
  limit = 200,
): Promise<SmsConversationRow[]> {
  const data = await call<SmsConversationRow[] | null>(
    "sms_conversation_list",
    { p_tab: tab, p_limit: Math.max(1, Math.min(200, limit)) },
  );
  return Array.isArray(data) ? data : [];
}

export async function getSmsConversationThread(
  conversationId: string,
  limit = 200,
  beforeSeq: number | null = null,
): Promise<SmsThread> {
  const data = await call<SmsThread>("sms_conversation_thread", {
    p_conversation_id: conversationId,
    p_limit: Math.max(1, Math.min(200, limit)),
    p_before_seq: beforeSeq,
  });
  return { ...data, items: Array.isArray(data?.items) ? data.items : [] };
}

/** True only when the page is visible AND focused — a background tab never clears a reminder. */
export function isForegroundView(): boolean {
  if (typeof document === "undefined") {
    return false;
  }
  return document.visibilityState === "visible" && document.hasFocus();
}

export function ackSmsConversation(
  snapshotId: string,
  foreground: boolean,
): Promise<SmsAckResult> {
  return call<SmsAckResult>("sms_conversation_ack", {
    p_snapshot_id: snapshotId,
    p_foreground: foreground,
  });
}

export function markSmsConversationUnread(
  conversationId: string,
): Promise<{ ok: boolean }> {
  return call("sms_conversation_mark_unread", {
    p_conversation_id: conversationId,
  });
}

export function markSmsConversationRead(
  conversationId: string,
): Promise<{ ok: boolean }> {
  return call("sms_conversation_mark_read", {
    p_conversation_id: conversationId,
  });
}

export function markSmsConversationNoReplyNeeded(
  snapshotId: string,
): Promise<{ ok: boolean; addressed?: number }> {
  return call("sms_conversation_no_reply_needed", {
    p_snapshot_id: snapshotId,
  });
}
